import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DatePicker from "@/components/DatePicker";
import SortableZoneList from "@/components/SortableZoneList";
import { isManagerTier } from "@/lib/roles";
import { ArrowLeft, Send } from "lucide-react";

export default function DisplayNew() {
  const nav = useNavigate();
  const [stores, setStores] = useState([]);
  const [managers, setManagers] = useState([]);
  const [zones, setZones] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueTime, setDueTime] = useState("20:00");
  const [instructions, setInstructions] = useState("");
  const [zoneIds, setZoneIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, e, z] = await Promise.all([
        api.get("/stores"), api.get("/employees"), api.get("/zones"),
      ]);
      setStores(s.data);
      setManagers(e.data.filter((u) => isManagerTier(u.role)));
      setZones(z.data);
      if (s.data.length && !storeId) setStoreId(s.data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const storeZones = useMemo(() => zones.filter((z) => z.active !== false && (!storeId || z.store_id === storeId)), [zones, storeId]);

  const toggleZone = (id) => setZoneIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const allZones = () => setZoneIds(storeZones.map((z) => z.id));
  const noZones = () => setZoneIds([]);

  const submit = async () => {
    if (!storeId) return toast.error("Pick a store");
    if (!managerId) return toast.error("Pick a manager");
    if (zoneIds.length === 0) return toast.error("Select at least one zone");
    setBusy(true);
    try {
      const due_at = dueTime ? `${inspectionDate}T${dueTime}:00` : null;
      const { data } = await api.post("/display_checks", {
        store_id: storeId, manager_id: managerId,
        inspection_date: inspectionDate, due_at,
        zone_ids: zoneIds, instructions,
      });
      toast.success(`Display Check ${data.check_number} created`);
      nav(`/display/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="dc-new">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="label-caps">Assignment</div>
        <h1 className="font-heading text-3xl sm:text-4xl">New Display Check</h1>
      </div>

      <div className="hos-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps">Store</label>
          <select className="hos-input" value={storeId} onChange={(e) => { setStoreId(e.target.value); setZoneIds([]); }} data-testid="dc-store">
            <option value="">— select —</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">Assign to Manager</label>
          <select className="hos-input" value={managerId} onChange={(e) => setManagerId(e.target.value)} data-testid="dc-manager">
            <option value="">— select —</option>
            {managers.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
          </select>
        </div>
        <div>
          <label className="label-caps">Inspection Date</label>
          <DatePicker value={inspectionDate} onChange={setInspectionDate} testId="dc-date"/>
        </div>
        <div>
          <label className="label-caps">Due Time</label>
          <input type="time" className="hos-input" value={dueTime} onChange={(e) => setDueTime(e.target.value)} data-testid="dc-due"/>
        </div>
        <div className="md:col-span-2">
          <label className="label-caps">Instructions</label>
          <textarea className="hos-input min-h-[80px]" placeholder="Make sure all products are properly faced, displays are fully stocked, pricing is visible..." value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="dc-instructions"/>
        </div>
      </div>

      <div className="hos-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading text-2xl">Zones ({zoneIds.length} of {storeZones.length})</h2>
          <div className="flex gap-2 text-xs">
            <button onClick={allZones} className="text-[color:var(--gold)]" data-testid="dc-all-zones">All Zones</button>
            <button onClick={noZones} className="text-[color:var(--text-secondary)]">None</button>
          </div>
        </div>
        {storeZones.length === 0 ? (
          <div className="text-sm text-[color:var(--text-secondary)]">No zones for this store. Add zones in Admin → Zones.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {storeZones.map((z) => {
              const active = zoneIds.includes(z.id);
              return (
                <label key={z.id} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${active ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10" : "border-[color:var(--border)]"}`}>
                  <input type="checkbox" checked={active} onChange={() => toggleZone(z.id)} data-testid={`dc-zone-${z.id}`} className="mt-1"/>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{z.name}</div>
                    <div className="text-xs text-[color:var(--text-secondary)]">
                      {z.floor && <>Floor: {z.floor} · </>}
                      {(z.fandom_ids || []).length} fandom{(z.fandom_ids || []).length === 1 ? "" : "s"}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {zoneIds.length > 1 && (
          <div className="mt-6 pt-4 border-t border-[color:var(--border)]">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <div className="label-caps">Walking Order</div>
                <div className="text-xs text-[color:var(--text-secondary)]">Drag to set the exact order managers should inspect the zones.</div>
              </div>
            </div>
            <SortableZoneList
              testIdPrefix="dc-new-order"
              items={zoneIds
                .map((id) => storeZones.find((z) => z.id === id))
                .filter(Boolean)
                .map((z) => ({ id: z.id, label: z.name, subtitle: z.floor ? `Floor: ${z.floor}` : undefined }))}
              onChange={setZoneIds}
              onRemove={(id) => setZoneIds((prev) => prev.filter((x) => x !== id))}
            />
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={submit} disabled={busy} className="hos-btn-primary flex items-center gap-2" data-testid="dc-create-btn">
          <Send size={16}/> Assign Display Check
        </button>
      </div>
    </div>
  );
}
